import React, { useState, useEffect } from 'react';
import { isTableAdmin as isTableAdminFn } from '../services/right.service';
import { useSelector, useDispatch } from 'react-redux';

import { Theme } from 'interfaces/theme.interface';
import { EventManager } from 'interfaces/eventManager.interface';
import { EventManagerService } from 'services/eventManagers.service';

import { EventManagerContent } from 'components/wrapperEventManager/eventManagerContent.component';
import ThemeModal from 'components/modals/themeModal.component';
import HomeLeftBar from '@common_components/layout/LeftBarManager';
import { KEY } from 'constants/local_storage_key.constant';
import { CommonGlobalService, JSONObject, MQTT_TOPICS, RootState, UUID, mqttSyncSubscriptions, useMqttSubscription, usePersistantState } from '@umanforge/common';
import { VisualisationService } from 'services/visualisation.service';
import EventManagerFooter from 'components/wrapperEventManager/eventManagerFooter.component';

function EventManagerView() {

  const me = useSelector( ( state: RootState ) => {return state.user?.value;} );

  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const dispatch = useDispatch();

  const VoidTheme = { id: '', eventManagerId: '', name: '', color: '', siteIds: [] };

  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const [ eventManagerId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_EVENT_MANAGER_ID, undefined );

  const [ eventManagerData, setEventManagerData ] = useState<EventManager | null>( null );
  const [ windowWidth, setWindowWidth ] = useState( window.innerWidth );
  const [ windowHeight, setWindowHeight ] = useState( window.innerHeight );

  const [ isPopupVisible, setPopupVisible ] = useState( false );
  const [ popupType, setPopupType ] = useState<'task' | 'theme' | null>( null );
  const [ selectedElementIndex, setSelectedElementIndex ] = useState<number | null>( null );

  const [ themeActive, setThemeActive ] = useState<Theme>( VoidTheme );
  const [ creationOfTheme, setCreationOfTheme ] = useState<boolean>( false );

  const [ themes, setThemes ] = useState<Theme[]>( [] );
  const [ isTableAdminOrHigher, setIsTableAdminOrHigher ] = useState<boolean>( false );

  const [ isViewMode, setViewMode ] = useState( visualisationToken != null );
  const [ isArchived, setIsArchived ] = useState( false );
  const [ showCharts, setShowCharts ] = useState( false );
  const [ themeHightlighted, setThemeHightlighted ] = useState<string>();
  const [ eventIdToOpen, setEventIdToOpen ] = useState<string | null>( null );

  // Extraire eventId et engagementId de la query string et l'ouvrir automatiquement
  const [ engagementIdToOpen, setEngagementIdToOpen ] = React.useState<string | null>( null );

  useEffect( () => {
    const handleClick = () => {
      setThemeHightlighted( prev => {
        if ( prev !== undefined ) {
          return undefined;
        }
        return prev;
      } );
    };

    window.addEventListener( 'mousedown', handleClick );

    return () => {
      window.removeEventListener( 'mousedown', handleClick );
    };
  }, [] );

  useEffect( () => {
    if ( visualisationToken && !isViewMode ) {
      setViewMode( true );
    }
  }, [ visualisationToken ] );

  useEffect( () => {
    if ( eventManagerId ) {
      fetchEventManagerData( eventManagerId );
    }
  }, [ eventManagerId ] );

  useEffect( () => {
    const searchParams = new URLSearchParams( location.search );
    const eventId = searchParams.get( 'eventId' );
    const engagementId = searchParams.get( 'engagementId' );
    if ( eventId ) {
      setEventIdToOpen( eventId );
    }
    if ( engagementId ) {
      setEngagementIdToOpen( engagementId );
    }
  }, [ location.search ] );

  useEffect( () => {
    if ( eventManagerData && me && selectedSectorId ) {
      const isUserAdminOfSectorValue = CommonGlobalService.isAdminOfSector( me, selectedSectorId );
      const adminsOfEventManager = ( eventManagerData?.admins ?? [] ).map( user => { return { userId: user.id, isAdmin: true }; } );
      const isTableAdmin = isTableAdminFn( adminsOfEventManager, me.id );
      setIsTableAdminOrHigher( isTableAdmin || isUserAdminOfSectorValue );
    }
  }, [ eventManagerData, me, selectedSectorId ] );

  useEffect( () => {
    const barPos = eventManagerData?.navbarPosition as 'auto' | 'left' | 'top' || 'left';
    if ( barPos === 'auto' ) {
      const handleResize = () => {
        setWindowWidth( window.innerWidth );
        setWindowHeight( window.innerHeight );
      };

      window.addEventListener( 'resize', handleResize );
      return () => {
        window.removeEventListener( 'resize', handleResize );
      };
    }
  }, [ eventManagerData?.navbarPosition ] );

  //----------- FETCH EVENT MANAGER DATA ---------------\\
  const getEffectiveBarPosition = (): 'left' | 'top' => {
    const barPos = eventManagerData?.navbarPosition as 'auto' | 'left' | 'top' || 'left';
    if ( barPos === 'auto' ) {
      return windowWidth > windowHeight ? 'left' : 'top';
    }
    return barPos as 'left' | 'top';
  };

  const fetchEventManagerData = async ( newEventManagerId: UUID | undefined ) => {
    let updatedEventManager: EventManager | null = null;
    try {
      if ( newEventManagerId ) {
        if ( isViewMode ) {
          updatedEventManager = await VisualisationService.getEventManager( newEventManagerId, visualisationToken, dispatch );
        } else {
          updatedEventManager = await EventManagerService.getEventManagerById( newEventManagerId, token, dispatch );
        }
        const odlIds = [];
        if ( eventManagerData ) {
          odlIds.push( eventManagerData );
        }
        mqttSyncSubscriptions( odlIds, [ updatedEventManager ], MQTT_TOPICS.fmEventManager );
      }
    } catch ( error ) {
      console.error( 'Failed to fetch event manager data:', error );
    } finally {
      handleEventManagerUpdate( updatedEventManager );
      setThemes( updatedEventManager?.themes ?? [] );
    }
  };

  useMqttSubscription( ( msg: JSONObject, category: string | null ) => {
    const payload = msg.payload as { eventManagerId?: string };
    const deletedEventManagerId = sessionStorage.getItem( KEY.DELETED_EVENT_MANAGER_ID );
    if ( deletedEventManagerId && payload.eventManagerId === deletedEventManagerId ) {
      return;
    }
    if ( category === 'event_manager' && eventManagerId && payload.eventManagerId != null && eventManagerId === payload.eventManagerId ) {
      fetchEventManagerData( eventManagerId );
    }
  } );

  //----------- HANDLE EVENT MANAGER UPDATE FROM CHILD ---------------\\
  const handleEventManagerUpdate = ( updatedEventManager: EventManager | null ) => {
    setEventManagerData( updatedEventManager );
  };

  //----------- OPEN THE POPUP OF THEMES ---------------\\
  const togglePopupWithElement = ( index?: number, theme?: Theme ) => {
    if ( !isViewMode ) {
      setPopupVisible( true );
      setPopupType( 'theme' );
      if ( index != undefined && theme != undefined ) {
        setThemeActive( theme );
        setSelectedElementIndex( index );
        setCreationOfTheme( false );
      } else {
        setCreationOfTheme( true );
        setThemeActive( VoidTheme );
        setSelectedElementIndex( 0 );
      }
    }
  };

  return (
    <div className='flex h-full w-full'>
      <div className={`flex flex-col min-h-screen w-full ${ isArchived ? 'bg-violet-50' : 'bg-gray-100' } text-black`}>

        <HomeLeftBar variant='table' hide={isViewMode} barPosition={eventManagerData?.navbarPosition as 'auto' | 'left' | 'top' || 'left'}/>
        {/*-------- Table (users and events) -----------*/}
        <div className='w-full'>
          <EventManagerContent
            isViewMode={ isViewMode }
            themeHightlighted={themeHightlighted}
            eventManager={eventManagerData}
            updateEventManager={fetchEventManagerData}
            isArchived={ isArchived }
            setIsArchived={ setIsArchived }
            navbarPosition={getEffectiveBarPosition()}
            onEventManagerUpdate={handleEventManagerUpdate}
            isAdminOfEventManager={isTableAdminOrHigher}
            eventIdToOpen={eventIdToOpen}
            engagementIdToOpen={engagementIdToOpen}
            setSelectedSectorId={ setSelectedSectorId }
            onEventOpened={() => {return setEventIdToOpen( null );}}
            onChartsToggle={ setShowCharts }
          />
        </div>

        <EventManagerFooter
          themes={ themes }
          isLeftNavBar={ !isViewMode && getEffectiveBarPosition() === 'left' }
          hide={ showCharts }
          addTheme={ !isViewMode && isTableAdminOrHigher ? togglePopupWithElement : undefined }
          onDoubleClick={ isTableAdminOrHigher ? togglePopupWithElement : undefined }
          onSingleClick={ theme => { setThemeHightlighted( theme.color ); } }
        />

      </div>

      {/*-------- Popup for Themes -----------*/}
      {isPopupVisible && popupType === 'theme' && selectedElementIndex !== null && (
        <ThemeModal
          disable={ isViewMode }
          creationOfTheme={creationOfTheme}
          themeActive={themeActive}
          elementIndex={selectedElementIndex}
          onUpdate={ action => {
            if ( action === 'update' || action === 'create' || action === 'merge' ) {
              fetchEventManagerData( eventManagerData?.id );
            }
          } }
          onClose={() => {
            setPopupVisible( false );
          }}
        />
      )}

    </div>
  );
}

export default EventManagerView;
